// [PATCH vasco-sync-fe-klon v1] KLON iz opal/OpalVezava.tsx (md5 eecce321, 14.09.2026) — Vasco veriga je VZPOREDNA Opalovi; Opal komponente se ne spreminjajo.
/* [PATCH multi-opal v1] Vezava podjetja na Vascovo bazo (naprava + baza)
   — razdelek v zavihku Sinhronizacija (NACRT_multi_opal_vezava_v1 §4).

   Endpointi (api/vasco_sync.py):
     GET  /vasco-sync/vezava/{company_id}          stanje + naprave/baze SERVISA
     POST /vasco-sync/vezava/{company_id}          {naprava_id, vasco_baza, potrdi_prevezavo}
     POST /vasco-sync/odvezava/{company_id}
     GET  /vasco-sync/naprave                      (superadmin: vse; tu filtrirano po servisu)
     POST /vasco-sync/naprave                      {tenant_id, oznaka}  -> zeton (enkrat)   [superadmin]
     POST /vasco-sync/naprave/{id}/ponastavi-zeton                                          [superadmin]
     POST /vasco-sync/naprave/{id}/aktivna         {aktivna}                                [superadmin]

   Racunovodja vidi in izbira SAMO naprave svojega servisa (streznik jih
   vrne po tenantu podjetja iz poti). Vklop pisanja (vasco_sync_pisi) tu
   NAMENOMA ni — ostane rocni korak po suhem teku (odlocitev O3).

   [PATCH multi-opal v1b] 09.09.2026 (po UI testu):
     - osveziSignal: gumb za osvezitev NA STRANI osvezi tudi to kartico
     - onSprememba: po vezavi/odvezavi se osvezi seznam cakalnice na strani
     - brez podvojenega imena racunalnika in brez praznih locil (·  ·)
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Link2, Unlink, Server, KeyRound, Copy, Loader, AlertTriangle, Check,
  RefreshCw, Plus, Power,
} from 'lucide-react';
import { api } from '../../lib/api';

type Baza = {
  vasco_baza: string;
  davcna: string | null;
  naziv_vasco: string | null;
  status: string;
  vezano_company_id: string | null;
  vezano_podjetje: string | null;
  to_podjetje: boolean;
  predlog: boolean;
};

type Naprava = {
  id: number;
  tenant_id: string;
  oznaka: string;
  ime_racunalnika: string | null;
  aktivna: boolean;
  ima_zeton: boolean;
  zadnji_stik: string | null;
  st_baz: number;
  st_vezav: number;
  baze: Baza[];
};

type Vezava = {
  par_id: number;
  naprava_id: number;
  naprava: string | null;
  ime_racunalnika: string | null;
  vasco_baza: string;
  davcna: string | null;
  naziv_vasco: string | null;
  status: string;
  vasco_sync_pisi: boolean;
  potrdil: string | null;
  potrjeno_ob: string | null;
  ogledalo: Record<string, number>;
  zadnji_paket: string | null;
};

type Stanje = {
  company_id: string;
  podjetje: string;
  davcna_podjetja: string | null;
  tenant_id?: string;
  vezava: Vezava | null;
  naprave: Naprava[];
};

const fmtCas = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('sl-SI', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const napakaBesedilo = (e: any, privzeto: string) =>
  String(e?.response?.data?.detail || e?.message || privzeto);

const gumb = 'px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50';
const gumbPrimarni = clsx(gumb, 'bg-blue-600 text-white hover:bg-blue-700');
const gumbNevtralni = clsx(gumb, 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400');
const gumbNevaren = clsx(gumb, 'border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/10');
const polje = 'w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

/* [PATCH multi-opal v1b] "Vasco streznik (VASCO)" + ime racunalnika "VASCO" ne
   sme dati "Vasco streznik (VASCO) (VASCO)"; prazna polja ne smejo pustiti "·". */
const imeNaprave = (n: { oznaka: string; ime_racunalnika?: string | null }) =>
  (n.ime_racunalnika && !n.oznaka.includes(n.ime_racunalnika))
    ? `${n.oznaka} (${n.ime_racunalnika})` : n.oznaka;

const zdruzi = (...deli: (string | null | undefined)[]) =>
  deli.map((d) => (d == null ? '' : String(d).trim())).filter(Boolean).join(' · ');

export function VascoVezava({ companyId, companyName, osveziSignal, onSprememba }: {
  companyId: string; companyName: string;
  osveziSignal?: number;              // [PATCH multi-opal v1b] osvezitev s strani
  onSprememba?: () => void;           // [PATCH multi-opal v1b] osvezi cakalnico
}) {
  const [stanje, setStanje] = useState<Stanje | null>(null);
  const [nalagam, setNalagam] = useState(true);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [jeVasco, setJeVasco] = useState<boolean | null>(null);   // [PATCH vasco-sync-fe v1] prikaz samo pri izvoz_program='vasco'
  useEffect(() => {
    setJeVasco(null);
    api.get(`/profil/${companyId}`).then(r => setJeVasco(r.data?.izvoz_program === 'vasco')).catch(() => setJeVasco(false));
  }, [companyId]);   // [PATCH vasco-sync-fe v1]
  const [jeSuperadmin, setJeSuperadmin] = useState(false);
  const [izvajam, setIzvajam] = useState(false);

  const [izbranaNaprava, setIzbranaNaprava] = useState<number | ''>('');
  const [izbranaBaza, setIzbranaBaza] = useState('');
  const [dialog, setDialog] = useState<'prevezi' | 'odvezi' | null>(null);

  // naprave (superadmin)
  const [novaOznaka, setNovaOznaka] = useState('');
  const [prikazanZeton, setPrikazanZeton] = useState<{ naprava: string; zeton: string } | null>(null);
  const [zetonDialog, setZetonDialog] = useState<Naprava | null>(null);

  useEffect(() => {
    api.get('/auth/me').then((r: any) => setJeSuperadmin(!!r.data?.is_superadmin)).catch(() => {});
  }, []);

  const nalozi = useCallback(() => {
    setNalagam(true); setNapaka(null);
    api.get('/vasco-sync/vezava/' + companyId)
      .then((r: any) => {
        const s: Stanje = r.data;
        setStanje(s);
        // predizbira: obstojeca vezava, sicer prva naprava + predlog po davcni
        if (s.vezava) {
          setIzbranaNaprava(s.vezava.naprava_id); setIzbranaBaza(s.vezava.vasco_baza);
        } else {
          const zPredlogom = s.naprave.find((n) => n.baze.some((b) => b.predlog));
          const n0 = zPredlogom || s.naprave[0];
          setIzbranaNaprava(n0 ? n0.id : '');
          const p = n0?.baze.find((b) => b.predlog);
          setIzbranaBaza(p ? p.vasco_baza : '');
        }
      })
      .catch((e: any) => setNapaka(napakaBesedilo(e, 'Stanja vezave ni bilo mogoče naložiti.')))
      .finally(() => setNalagam(false));
  }, [companyId]);

  useEffect(() => { nalozi(); }, [nalozi]);
  // [PATCH multi-opal v1b] gumb za osvezitev na strani osvezi tudi kartico
  useEffect(() => { if (osveziSignal !== undefined) nalozi(); }, [osveziSignal, nalozi]);

  const naprava = useMemo(
    () => stanje?.naprave.find((n) => n.id === izbranaNaprava) || null,
    [stanje, izbranaNaprava]);
  const baza = useMemo(
    () => naprava?.baze.find((b) => b.vasco_baza === izbranaBaza) || null,
    [naprava, izbranaBaza]);

  const vezava = stanje?.vezava || null;
  const jeIstaVezava = !!vezava && vezava.naprava_id === izbranaNaprava && vezava.vasco_baza === izbranaBaza;
  const bazaZasedena = !!baza && !!baza.vezano_company_id && !baza.to_podjetje;
  const davcnaNeUjema = !!baza && !!stanje?.davcna_podjetja && baza.davcna !== stanje.davcna_podjetja;
  const smeVezati = !!naprava && !!baza && !jeIstaVezava && !bazaZasedena && !davcnaNeUjema && !izvajam;

  const vezi = async (potrdiPrevezavo: boolean) => {
    if (!naprava || !baza) return;
    setIzvajam(true);
    try {
      const r = await api.post('/vasco-sync/vezava/' + companyId, {
        naprava_id: naprava.id, vasco_baza: baza.vasco_baza, potrdi_prevezavo: potrdiPrevezavo,
      });
      const d = r.data || {};
      toast.success(d.prevezava
        ? `Prevezano na ${baza.vasco_baza} (ogledalo izpraznjeno: ${d.ogledalo_izbrisano ?? 0}, čakalnica zaprta: ${d.cakalnica_zaprta ?? 0})`
        : `Podjetje vezano na Vascovo bazo ${baza.vasco_baza}`);
      setDialog(null);
      nalozi();
      onSprememba?.();                 // [PATCH multi-opal v1b] osvezi cakalnico
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409 && !potrdiPrevezavo && vezava) {
        setDialog('prevezi');           // streznik zahteva izrecno potrditev prevezave
      } else {
        toast.error(napakaBesedilo(e, 'Vezava ni uspela.'));
      }
    } finally {
      setIzvajam(false);
    }
  };

  const odvezi = async () => {
    setIzvajam(true);
    try {
      const r = await api.post('/vasco-sync/odvezava/' + companyId);
      const d = r.data || {};
      toast.success(`Odvezano (ogledalo izpraznjeno: ${d.ogledalo_izbrisano ?? 0}, čakalnica zaprta: ${d.cakalnica_zaprta ?? 0})`);
      setDialog(null);
      nalozi();
      onSprememba?.();                 // [PATCH multi-opal v1b] osvezi cakalnico
    } catch (e: any) {
      toast.error(napakaBesedilo(e, 'Odvezava ni uspela.'));
    } finally {
      setIzvajam(false);
    }
  };

  // --- naprave (superadmin) -------------------------------------------
  const tenantId = stanje?.tenant_id || stanje?.naprave[0]?.tenant_id || '';

  const novaNaprava = async () => {
    if (!novaOznaka.trim() || !tenantId) return;
    setIzvajam(true);
    try {
      const r = await api.post('/vasco-sync/naprave', { tenant_id: tenantId, oznaka: novaOznaka.trim() });
      setPrikazanZeton({ naprava: r.data?.naprava?.oznaka || novaOznaka, zeton: r.data?.naprava?.zeton || '' });
      setNovaOznaka('');
      nalozi();
    } catch (e: any) {
      toast.error(napakaBesedilo(e, 'Naprave ni bilo mogoče ustvariti.'));
    } finally {
      setIzvajam(false);
    }
  };

  const novZeton = async (n: Naprava) => {
    setIzvajam(true);
    try {
      const r = await api.post(`/vasco-sync/naprave/${n.id}/ponastavi-zeton`);
      setPrikazanZeton({ naprava: n.oznaka, zeton: r.data?.naprava?.zeton || '' });
      setZetonDialog(null);
      nalozi();
    } catch (e: any) {
      toast.error(napakaBesedilo(e, 'Žetona ni bilo mogoče ponastaviti.'));
    } finally {
      setIzvajam(false);
    }
  };

  const preklopiAktivno = async (n: Naprava) => {
    setIzvajam(true);
    try {
      await api.post(`/vasco-sync/naprave/${n.id}/aktivna`, { aktivna: !n.aktivna });
      toast.success(n.aktivna ? `Naprava ${n.oznaka} izklopljena` : `Naprava ${n.oznaka} vklopljena`);
      nalozi();
    } catch (e: any) {
      toast.error(napakaBesedilo(e, 'Spremembe ni bilo mogoče shraniti.'));
    } finally {
      setIzvajam(false);
    }
  };

  const kopiraj = async (v: string) => {
    try { await navigator.clipboard.writeText(v); toast.success('Kopirano'); }
    catch { toast.error('Kopiranje ni uspelo — označi in kopiraj ročno.'); }
  };

  // --- prikaz ------------------------------------------------------------
  if (jeVasco !== true) return null;   // [PATCH vasco-sync-fe v1] Vasco kartica samo za Vasco podjetja
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-indigo-500" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Vasco vezava</h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            podjetje ↔ (Vascov strežnik, baza) · davčna podjetja {stanje?.davcna_podjetja || '—'}
          </span>
        </div>
        <button onClick={nalozi} title="Osveži"
          className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
        </button>
      </div>

      {nalagam && !stanje ? (
        <div className="flex items-center justify-center h-24"><Loader className="w-5 h-5 text-blue-500 animate-spin" /></div>
      ) : napaka ? (
        <div className="px-5 py-6 text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> {napaka}
        </div>
      ) : stanje && (
        <div className="p-5 space-y-5">
          {/* trenutna vezava */}
          {vezava ? (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-500/10 p-4 text-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      Vezano na bazo <span className="font-mono">{vezava.vasco_baza}</span>
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      · {vezava.naprava ? imeNaprave({ oznaka: vezava.naprava, ime_racunalnika: vezava.ime_racunalnika }) : '—'}
                    </span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">
                    {zdruzi(vezava.naziv_vasco,
                      `davčna ${vezava.davcna || '—'}`,
                      `vezal ${vezava.potrdil || '—'} ${fmtCas(vezava.potrjeno_ob)}`)}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">
                    Pisanje v AlfaBerg:{' '}
                    <span className={clsx('font-medium', vezava.vasco_sync_pisi ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300')}>
                      {vezava.vasco_sync_pisi ? 'vklopljeno' : 'izklopljeno (vklop po suhem teku — superadmin)'}
                    </span>
                    {' '}· ogledalo: {Object.keys(vezava.ogledalo || {}).length
                      ? Object.entries(vezava.ogledalo).map(([k, v]) => `${k} ${v}`).join(', ')
                      : 'prazno (čaka prvi prenos)'}
                    {' '}· zadnji paket {fmtCas(vezava.zadnji_paket)}
                  </div>
                </div>
                <button onClick={() => setDialog('odvezi')} disabled={izvajam} className={gumbNevaren}>
                  <Unlink className="w-4 h-4" /> Odveži
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-500/10 p-4 text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Podjetje še ni vezano na nobeno Vascovo bazo — sinhronizacija ne teče.
            </div>
          )}

          {/* izbira naprave in baze */}
          {stanje.naprave.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Servis še nima registrirane naprave z Vascom. {jeSuperadmin ? 'Dodaj jo spodaj.' : 'Obrni se na skrbnika AlfaBerga.'}
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto] items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Naprava (Vascov strežnik)</span>
                <select className={polje} value={izbranaNaprava}
                  onChange={(e) => { setIzbranaNaprava(Number(e.target.value)); setIzbranaBaza(''); }}>
                  {stanje.naprave.map((n) => (
                    <option key={n.id} value={n.id} disabled={!n.aktivna}>
                      {zdruzi(imeNaprave(n), `${n.st_baz} baz`,
                        !n.aktivna ? 'izklopljena' : null,
                        !n.ima_zeton ? 'brez žetona' : null)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Vascova baza {naprava && naprava.baze.some((b) => b.predlog) ? '· predlog po davčni je označen z ★' : ''}
                </span>
                <select className={polje} value={izbranaBaza} onChange={(e) => setIzbranaBaza(e.target.value)}>
                  <option value="">— izberi bazo —</option>
                  {(naprava?.baze || []).map((b) => (
                    <option key={b.vasco_baza} value={b.vasco_baza}
                      disabled={!!b.vezano_company_id && !b.to_podjetje}>
                      {(b.predlog ? '★ ' : '') + zdruzi(
                        b.vasco_baza, b.davcna || 'brez davčne', b.naziv_vasco,
                        b.vezano_company_id ? (b.to_podjetje ? '(to podjetje)' : `vezano: ${b.vezano_podjetje}`) : null)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={() => (vezava && !jeIstaVezava ? setDialog('prevezi') : vezi(false))}
                disabled={!smeVezati} className={gumbPrimarni}
                title={davcnaNeUjema ? 'Davčna baze se ne ujema z davčno podjetja' : bazaZasedena ? 'Baza je vezana na drugo podjetje' : ''}>
                {izvajam ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {vezava && !jeIstaVezava ? 'Preveži' : 'Veži'}
              </button>
            </div>
          )}
          {davcnaNeUjema && baza && (
            <p className="text-xs text-rose-600 dark:text-rose-400">
              Davčna baze {baza.vasco_baza} ({baza.davcna || '—'}) se ne ujema z davčno podjetja ({stanje.davcna_podjetja}). Vezava bi bila zavrnjena; preveri podatke v Vascu ali v AlfaBergu.
            </p>
          )}

          {/* naprave — superadmin */}
          {jeSuperadmin && (
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Naprave servisa</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">(samo superadmin: žetoni, vklop/izklop)</span>
              </div>
              {stanje.naprave.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 dark:text-gray-400">
                      <tr className="text-left">
                        <th className="py-1 pr-3">Naprava</th><th className="py-1 pr-3">Računalnik</th>
                        <th className="py-1 pr-3">Baz / vezav</th><th className="py-1 pr-3">Zadnji stik</th>
                        <th className="py-1 pr-3">Stanje</th><th className="py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stanje.naprave.map((n) => (
                        <tr key={n.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{n.oznaka} <span className="text-gray-400">#{n.id}</span></td>
                          <td className="py-2 pr-3 font-mono text-gray-700 dark:text-gray-300">{n.ime_racunalnika || '—'}</td>
                          <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{n.st_baz} / {n.st_vezav}</td>
                          <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{fmtCas(n.zadnji_stik)}</td>
                          <td className="py-2 pr-3">
                            <span className={clsx('px-2 py-0.5 rounded-full text-xs',
                              !n.aktivna ? 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                                : n.ima_zeton ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                                  : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300')}>
                              {!n.aktivna ? 'izklopljena' : n.ima_zeton ? 'aktivna' : 'brez žetona'}
                            </span>
                          </td>
                          <td className="py-2 flex gap-2 justify-end">
                            <button onClick={() => setZetonDialog(n)} disabled={izvajam} className={gumbNevtralni} title="Izdaj nov žeton (stari neha veljati)">
                              <KeyRound className="w-4 h-4" /> {n.ima_zeton ? 'Nov žeton' : 'Izdaj žeton'}
                            </button>
                            <button onClick={() => preklopiAktivno(n)} disabled={izvajam} className={n.aktivna ? gumbNevaren : gumbNevtralni}>
                              <Power className="w-4 h-4" /> {n.aktivna ? 'Izklopi' : 'Vklopi'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <label className="text-sm flex-1 max-w-md">
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Nova naprava (oznaka, npr. »BIRO Vasco strežnik«)</span>
                  <input className={polje} value={novaOznaka} onChange={(e) => setNovaOznaka(e.target.value)} maxLength={120} />
                </label>
                <button onClick={novaNaprava} disabled={izvajam || novaOznaka.trim().length < 2 || !tenantId} className={gumbPrimarni}>
                  <Plus className="w-4 h-4" /> Dodaj napravo in izdaj žeton
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* dialogi */}
      {dialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !izvajam && setDialog(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {dialog === 'prevezi' ? `Preveži podjetje na ${izbranaBaza}` : `Odveži podjetje od ${vezava?.vasco_baza}`}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {dialog === 'prevezi'
                ? `Stari vir (${vezava?.vasco_baza} · ${vezava?.naprava || ''}) se odklopi. Ogledalo podjetja se izprazni, odprte postavke čakalnice se zaprejo, nova baza naredi poln prenos. Pisanje v AlfaBerg ostane izklopljeno, dokler ga superadmin po suhem teku ne vklopi.`
                : 'Sinhronizacija se ustavi. Ogledalo podjetja se izprazni, odprte postavke čakalnice se zaprejo. Podatki partnerjev v AlfaBergu ostanejo nespremenjeni.'}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDialog(null)} disabled={izvajam} className={gumbNevtralni}>Prekliči</button>
              <button onClick={() => (dialog === 'prevezi' ? vezi(true) : odvezi())} disabled={izvajam}
                className={dialog === 'prevezi' ? gumbPrimarni : gumbNevaren}>
                {izvajam ? <Loader className="w-4 h-4 animate-spin" /> : dialog === 'prevezi' ? <Link2 className="w-4 h-4" /> : <Unlink className="w-4 h-4" />}
                {dialog === 'prevezi' ? 'Da, preveži' : 'Da, odveži'}
              </button>
            </div>
          </div>
        </div>
      )}

      {zetonDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !izvajam && setZetonDialog(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 dark:text-white">Nov žeton za {zetonDialog.oznaka}</h3>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              Stari žeton takoj neha veljati — agent na tem računalniku bo do vnosa novega žetona dobival 401. Žeton se prikaže samo enkrat.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setZetonDialog(null)} disabled={izvajam} className={gumbNevtralni}>Prekliči</button>
              <button onClick={() => novZeton(zetonDialog)} disabled={izvajam} className={gumbPrimarni}>
                <KeyRound className="w-4 h-4" /> Izdaj nov žeton
              </button>
            </div>
          </div>
        </div>
      )}

      {prikazanZeton && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-xl w-full p-5 space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">Žeton naprave {prikazanZeton.naprava}</h3>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              Prikazan je <b>samo enkrat</b>. Na računalniku z Vascom ga shrani v <code>zeton.txt</code> in poženi agenta z <code>-ShraniZeton</code>; nato datoteko izbriši.
            </p>
            <div className="flex gap-2">
              <input readOnly value={prikazanZeton.zeton} className={clsx(polje, 'font-mono text-xs')} onFocus={(e) => e.currentTarget.select()} />
              <button onClick={() => kopiraj(prikazanZeton.zeton)} className={gumbNevtralni}><Copy className="w-4 h-4" /> Kopiraj</button>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setPrikazanZeton(null)} className={gumbPrimarni}><Check className="w-4 h-4" /> Shranil sem ga</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
